import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";

/**
 * Строка списка: иконка — две строки текста — правый слот, снизу волосяная
 * линия. Последняя строка списка линию не рисует (`last:border-b-0`), поэтому
 * список не заканчивается «висящей» чертой над паддингом карточки.
 *
 * Два ритма из макетов: `compact` (12px, реестр Баллов — плотный поток
 * однотипных записей) и `default` (14px, Legal center — строки с действиями).
 */
// Текстовая колонка задаёт высоту строки и прижата к верху; иконка, мета и
// правый слот центрируются относительно неё (`self-center`), а не висят у
// верхней кромки, когда подпись переносится на две строки.
const listRowVariants = cva("flex items-center border-hairline border-b last:border-b-0", {
	variants: {
		density: {
			compact: "gap-3 py-3",
			default: "gap-3.5 py-3.5",
		},
		interactive: {
			true: [
				"iec-press-glyph -mx-2 cursor-pointer rounded-control px-2 text-left transition-colors duration-150 ease-smooth",
				"hover:bg-sunken",
				"focus-ring",
			],
			false: "",
		},
	},
	defaultVariants: { density: "compact", interactive: false },
});

/**
 * То же для строки, у которой есть собственное действие: рамку, отступы,
 * скругление и заливку берёт на себя ОБЁРТКА (§ ниже), поэтому кнопка-строка
 * остаётся прозрачной и без полей. Иначе подсветка непрочитанного кончалась бы
 * на кнопке, а действие висело снаружи подсветки.
 */
const listRowInnerVariants = cva("flex items-center border-0 bg-transparent text-left", {
	variants: {
		density: {
			compact: "gap-3 py-3",
			default: "gap-3.5 py-3.5",
		},
	},
	defaultVariants: { density: "compact" },
});

interface ListRowContent {
	/** Обычно IconTile или голая иконка — макеты используют оба варианта. */
	icon?: ReactNode;
	title: ReactNode;
	subtitle?: ReactNode;
	/** Между текстом и значением: чип, бейдж, кнопка. */
	meta?: ReactNode;
	/** Крайний правый слот: сумма, шеврон, переключатель. */
	trailing?: ReactNode;
	/**
	 * Собственное действие строки — кнопка, живущая РЯДОМ с ней, а не внутри.
	 *
	 * Отдельный слот, а не «положите кнопку в `trailing`», потому что строка с
	 * `onSelect` рендерится КНОПКОЙ: вложенная кнопка — невалидная разметка, и
	 * клик по ней всё равно достался бы внешней. Здесь действие выносится из
	 * кнопки-строки в общую обёртку, поэтому у него свой клик и своя фокусная
	 * остановка.
	 */
	action?: ReactNode;
}

export interface ListRowProps
	extends Omit<HTMLAttributes<HTMLDivElement>, "title">,
		VariantProps<typeof listRowVariants>,
		ListRowContent {
	/** Строка ведёт куда-то — тогда она рендерится кнопкой и ловит клавиатуру. */
	onSelect?: () => void;
}

export function ListRow({
	icon,
	title,
	subtitle,
	meta,
	trailing,
	action,
	density,
	className,
	onSelect,
	...rest
}: ListRowProps) {
	const body = (
		<>
			{icon != null ? <span className="flex shrink-0 self-center">{icon}</span> : null}
			<div className="flex min-w-0 flex-grow flex-col gap-0.5 self-start">
				<span className="t-row min-w-0 break-words">{title}</span>
				{subtitle != null ? (
					<span className="t-meta min-w-0 break-words text-fg-muted">{subtitle}</span>
				) : null}
			</div>
			{meta != null ? <span className="flex shrink-0 self-center">{meta}</span> : null}
			{trailing != null ? <span className="flex shrink-0 self-center">{trailing}</span> : null}
		</>
	);

	if (onSelect && action) {
		// Действие стоит СЕСТРОЙ кнопки-строки, а не внутри неё: разметка
		// остаётся валидной, а клик по действию не открывает строку.
		//
		// Всё оформление при этом на ОБЁРТКЕ, а не на кнопке: заливку строки
		// задаёт вызывающий (подсветка непрочитанного), и на кнопке она
		// обрывалась бы ровно перед действием — кнопка «отметить прочитанным»
		// оказывалась вне собственной подсветки.
		return (
			<div
				className={cn(
					// `gap-3` между строкой и действием, а не `gap-1`: правый край
					// строки — это мета («IEC HERO · 55 МИНУТ НАЗАД»), и вплотную
					// к ней кнопка читается как часть подписи, а не как действие.
					"-mx-2 flex items-center gap-3 rounded-control border-hairline border-b px-2 last:border-b-0",
					"transition-colors duration-150 ease-smooth hover:bg-sunken",
					className,
				)}
			>
				<button
					type="button"
					onClick={onSelect}
					className={cn(
						listRowInnerVariants({ density }),
						// Пружина глифа — на самой строке, а не на обёртке: иначе
						// иконка дёргалась бы и при нажатии на действие.
						"iec-press-glyph focus-ring min-w-0 flex-grow cursor-pointer",
					)}
				>
					{body}
				</button>
				<span className="flex shrink-0 items-center">{action}</span>
			</div>
		);
	}

	if (onSelect) {
		return (
			<button
				type="button"
				onClick={onSelect}
				className={cn(listRowVariants({ density, interactive: true }), className)}
			>
				{body}
			</button>
		);
	}

	return (
		<div className={cn(listRowVariants({ density }), className)} {...rest}>
			{body}
		</div>
	);
}

export { listRowVariants };
